import { Injectable } from '@angular/core';

export type ZoomPlatform = 'android' | 'ios' | 'desktop';
export type ZoomBrowser = 'chrome' | 'edge' | 'firefox' | 'safari' | 'other';
export type MicTestResult = 'ok' | 'denied' | 'no-device' | 'busy' | 'unsupported' | 'error';

/**
 * Opens Zoom meetings through the standard Zoom join link (https://...zoom.us/j/<id>?pwd=...).
 * Zoom's own page then offers "Open Zoom Meetings?" / "Join from Zoom Workplace app" / "Join from browser",
 * so the browser decides whether the installed app opens.
 */
@Injectable({
  providedIn: 'root'
})
export class ZoomLauncherService {

  /**
   * In-app browsers (WhatsApp, Instagram, Facebook, generic Android WebView) usually block
   * or silently skip the microphone/camera prompt, so Zoom in the browser ends up muted.
   */
  isInAppBrowser(): boolean {
    const ua = navigator.userAgent || '';
    return /FBAN|FBAV|FB_IAB|Instagram|WhatsApp|Line\/|Snapchat|; wv\)/i.test(ua);
  }

  /**
   * The admin's join URL already carries the encrypted passcode, so the user is not asked for it.
   * uname pre-fills the display name in Zoom.
   */
  buildJoinUrl(details: any, displayName: string): string {
    const meetingId = String(details.zoom_meeting_id || '').replace(/\s/g, '');
    let url = details.join_url && /^https?:\/\//i.test(details.join_url)
      ? details.join_url.trim()
      : `https://zoom.us/j/${meetingId}` + (details.passcode ? `?pwd=${encodeURIComponent(details.passcode)}` : '');

    const hashAt = url.indexOf('#');
    const hash = hashAt >= 0 ? url.substring(hashAt) : '';
    url = hashAt >= 0 ? url.substring(0, hashAt) : url;
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'uname=' + encodeURIComponent(displayName) + hash;
  }

  getPlatform(): ZoomPlatform {
    const ua = navigator.userAgent || '';
    if (/Android/i.test(ua)) {
      return 'android';
    }
    // iPadOS 13+ reports itself as a Mac, so check for touch support too
    if (/iPhone|iPad|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
      return 'ios';
    }
    return 'desktop';
  }

  getBrowser(): ZoomBrowser {
    const ua = navigator.userAgent || '';
    if (/Edg\//i.test(ua)) {
      return 'edge';
    }
    if (/Firefox|FxiOS/i.test(ua)) {
      return 'firefox';
    }
    if (/Chrome|CriOS/i.test(ua)) {
      return 'chrome';
    }
    if (/Safari/i.test(ua)) {
      return 'safari';
    }
    return 'other';
  }

  /**
   * Checks the microphone on our own site. It cannot grant zoom.us permission, but it
   * catches missing devices and OS level blocks that break Zoom audio in the browser too.
   */
  testMicrophone(): Promise<MicTestResult> {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return Promise.resolve('unsupported' as MicTestResult);
    }
    return navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => {
        stream.getTracks().forEach(track => track.stop());
        return 'ok' as MicTestResult;
      })
      .catch(err => {
        const name = err && err.name;
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          return 'denied' as MicTestResult;
        }
        if (name === 'NotFoundError' || name === 'OverconstrainedError') {
          return 'no-device' as MicTestResult;
        }
        if (name === 'NotReadableError' || name === 'AbortError') {
          return 'busy' as MicTestResult;
        }
        return 'error' as MicTestResult;
      });
  }

  /** Must be called directly from a click handler so the popup blocker allows the new tab. */
  openInNewTab(url: string) {
    const win = window.open(url, '_blank');
    if (!win) {
      window.location.href = url;
    }
  }
}
